import { useMemo, type ReactNode } from "react"
import { AiContext, type AiContextValue } from "connected-components/ai-teammate/context/ai-context"
import { useAppSelector } from "hooks/redux"
import { useCurrentDashboardId } from "./current-dashboard-id"
import { useInstanceWorkspaceContext } from "./instance-workspace-context"
import { useMountedContentId } from "./mounted-content"
import { DEFAULT_CORE_STATE } from "../store/core-slice"
import { selectIsDashboardStoreLoaded } from "../store/selectors/select-is-dashboard-store-loaded"
import { selectModelId } from "../store/selectors/select-model-id"

export function DashboardAiContextProvider({ children }: { children: ReactNode }) {
  const dashboardId = useCurrentDashboardId()
  const instanceWorkspace = useInstanceWorkspaceContext()
  const pinnableDashboardId = useMountedContentId("pin", dashboardId)
  const modelId = useAppSelector((s) => selectModelId(s, dashboardId))
  const core = useAppSelector((s) => (dashboardId === null ? DEFAULT_CORE_STATE : s.dashboard.core[dashboardId] ?? DEFAULT_CORE_STATE))
  const isStoreLoaded = useAppSelector((s) => dashboardId !== null && selectIsDashboardStoreLoaded(s, dashboardId))
  const isSeedCurrent = instanceWorkspace === null || instanceWorkspace.currentSeed !== null

  const aiContextValue = useMemo<AiContextValue>(() => ({
    modelId,
    instrument: core.instrument,
    base: core.base,
    startDate: core.startDate,
    endDate: core.endDate,
    isPreferencesLoaded: isStoreLoaded && isSeedCurrent,
    canPinArtifacts: pinnableDashboardId !== null,
    // Constant (not dashboardId): dismissal should hold across switching dashboards, not reset per-id.
    nudgeScopeKey: "dashboard",
  }), [
    modelId,
    core.instrument,
    core.base,
    core.startDate,
    core.endDate,
    isStoreLoaded,
    isSeedCurrent,
    pinnableDashboardId,
  ])

  return <AiContext.Provider value={aiContextValue}>{children}</AiContext.Provider>
}
